import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #vavoomTouchStartX = 0;
  #vavoomTouchStartY = 0;
  #vavoomLastY = 0;

  #vavoomOriginalParent = null;
  #vavoomOriginalNextSibling = null;
  #vavoomResizeRaf = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);

    this.refs.menuDrawer?.classList.add('vavoom-mobile-menu-drawer');

    this.refs.menuDrawer?.addEventListener('click', this.#vavoomOnDrawerClick);
    this.refs.menuDrawer?.addEventListener('touchstart', this.#vavoomOnTouchStart, { passive: true });
    this.refs.menuDrawer?.addEventListener('touchmove', this.#vavoomOnTouchMove, { passive: false });

    window.addEventListener('resize', this.#vavoomScheduleHeightSync, { passive: true });
    window.visualViewport?.addEventListener('resize', this.#vavoomScheduleHeightSync, { passive: true });

    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keyup', this.#onKeyUp);

    this.refs.menuDrawer?.removeEventListener('click', this.#vavoomOnDrawerClick);
    this.refs.menuDrawer?.removeEventListener('touchstart', this.#vavoomOnTouchStart);
    this.refs.menuDrawer?.removeEventListener('touchmove', this.#vavoomOnTouchMove);

    window.removeEventListener('resize', this.#vavoomScheduleHeightSync);
    window.visualViewport?.removeEventListener('resize', this.#vavoomScheduleHeightSync);

    if (this.#vavoomResizeRaf) {
      cancelAnimationFrame(this.#vavoomResizeRaf);
      this.#vavoomResizeRaf = 0;
    }

    this.#vavoomRestoreDrawer();
  }

  #vavoomIsMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  #vavoomPortalDrawer() {
    const drawer = this.refs.menuDrawer;
    if (!drawer || !this.#vavoomIsMobile()) return;

    drawer.classList.add('vavoom-mobile-menu-drawer');

    if (!this.#vavoomOriginalParent) {
      this.#vavoomOriginalParent = drawer.parentNode;
      this.#vavoomOriginalNextSibling = drawer.nextSibling;
    }

    if (drawer.parentNode !== document.body) {
      document.body.appendChild(drawer);
    }

    drawer.classList.add('vavoom-menu-drawer-portal');
    this.#vavoomSetDrawerHeight();
  }

  #vavoomRestoreDrawer() {
    const drawer = this.refs.menuDrawer;
    if (!drawer || !this.#vavoomOriginalParent) return;

    drawer.classList.remove('vavoom-menu-drawer-portal');

    if (this.#vavoomOriginalNextSibling && this.#vavoomOriginalNextSibling.parentNode === this.#vavoomOriginalParent) {
      this.#vavoomOriginalParent.insertBefore(drawer, this.#vavoomOriginalNextSibling);
    } else {
      this.#vavoomOriginalParent.appendChild(drawer);
    }

    this.#vavoomOriginalParent = null;
    this.#vavoomOriginalNextSibling = null;
  }

  #vavoomSetDrawerHeight() {
    const drawer = this.refs.menuDrawer;
    if (!drawer) return;

    const visualHeight = window.visualViewport?.height || 0;
    const innerHeight = window.innerHeight || 0;
    const clientHeight = document.documentElement.clientHeight || 0;
    const height = Math.ceil(Math.max(visualHeight, innerHeight, clientHeight));

    if (height > 0) {
      drawer.style.setProperty('--vavoom-drawer-height', `${height}px`);
    }
  }

  #vavoomScheduleHeightSync = () => {
    if (!this.isOpen) return;
    if (this.#vavoomResizeRaf) return;

    this.#vavoomResizeRaf = requestAnimationFrame(() => {
      this.#vavoomResizeRaf = 0;
      this.#vavoomSetDrawerHeight();
    });
  };

  #vavoomGetScroller(target) {
    if (!(target instanceof Element)) return this.refs.menuDrawer;

    const horizontal = target.closest('.menu-drawer__featured-content-list');
    if (horizontal) return horizontal;

    const submenu = target.closest('.menu-drawer__submenu');
    if (submenu) return submenu;

    return this.refs.menuDrawer;
  }

  #vavoomOnDrawerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const closeButton = event.target.closest('.menu-drawer__close-button');
    if (closeButton && this.refs.menuDrawer?.contains(closeButton)) {
      event.preventDefault();
      this.close();
      return;
    }

    const backButton = event.target.closest('.menu-drawer__back-button');
    if (backButton && this.refs.menuDrawer?.contains(backButton)) {
      event.preventDefault();
      const details = backButton.closest('details');
      this.#close(details || this.refs.details);
      return;
    }

    const mainSummary = event.target.closest('summary.menu-drawer__menu-item--mainlist');
    if (mainSummary && this.refs.menuDrawer?.contains(mainSummary)) {
      const details = mainSummary.closest('details');

      if (details && details !== this.refs.details && details.classList.contains('menu-drawer__menu-container')) {
        requestAnimationFrame(() => {
          if (!details.open) return;

          mainSummary.setAttribute('aria-expanded', 'true');
          details.classList.add('menu-open');
          this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
          trapFocus(details);
        });
      }
    }
  };

  #vavoomOnTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;

    this.#vavoomTouchStartX = touch.clientX;
    this.#vavoomTouchStartY = touch.clientY;
    this.#vavoomLastY = touch.clientY;
  };

  #vavoomOnTouchMove = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;

    const target = event.target;
    const dx = touch.clientX - this.#vavoomTouchStartX;
    const dyFromStart = touch.clientY - this.#vavoomTouchStartY;
    const dy = touch.clientY - this.#vavoomLastY;
    this.#vavoomLastY = touch.clientY;

    const horizontal = target instanceof Element ? target.closest('.menu-drawer__featured-content-list') : null;
    if (horizontal && Math.abs(dx) > Math.abs(dyFromStart)) {
      event.stopPropagation();
      return;
    }

    const scroller = this.#vavoomGetScroller(target);
    const maxScroll = scroller.scrollHeight - scroller.clientHeight;

    if (maxScroll <= 0) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const atTop = scroller.scrollTop <= 0;
    const atBottom = scroller.scrollTop >= maxScroll - 1;

    if ((atTop && dy > 0) || (atBottom && dy < 0)) {
      event.preventDefault();
    }

    event.stopPropagation();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      this.#vavoomPortalDrawer();
      this.#vavoomSetDrawerHeight();
    }

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (details === this.refs.details) {
        document.documentElement.classList.add('vavoom-menu-drawer-open');
        document.body.classList.add('vavoom-menu-drawer-open');
      }

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const drawer = details === this.refs.details ? this.refs.menuDrawer : details.querySelector('.menu-drawer__submenu');

      onAnimationEnd(drawer || details, () => {
        trapFocus(details === this.refs.details ? this.refs.menuDrawer : details);
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      document.documentElement.classList.remove('vavoom-menu-drawer-open');
      document.body.classList.remove('vavoom-menu-drawer-open');
    }

    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const drawer = details === this.refs.details ? this.refs.menuDrawer : details.querySelector('.menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.refs.menuDrawer.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          this.#vavoomRestoreDrawer();
        } else {
          trapFocus(this.refs.menuDrawer);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const root = details === this.refs.details ? this.refs.menuDrawer : details;
    const content = root.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}